'use client';
import React, { useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import useBodyScrollLock from '../asides/useBodyScrollLock';

interface OverlayProps {
  open: boolean;
  onClose: () => void;
  children: React.ReactNode;
  id?: string;
  ariaLabelledBy?: string;
  className?: string;
  role?: 'dialog' | 'alertdialog';
  asSlideOver?: boolean;
}

// Focus trap and ESC close
export const Overlay: React.FC<OverlayProps> = ({
  open,
  onClose,
  children,
  id,
  ariaLabelledBy,
  className = '',
  role = 'dialog',
  asSlideOver = false,
}) => {
  const ref = useRef<HTMLDivElement>(null);

  useBodyScrollLock(open);

  useEffect(() => {
    if (!open) return;
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handleKey);
    return () => document.removeEventListener('keydown', handleKey);
  }, [open, onClose]);

  useEffect(() => {
    if (!open) return;
    const node = ref.current;
    const focusable = node?.querySelector<HTMLElement>(
      'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])',
    );
    focusable?.focus();
  }, [open]);

  if (!open) return null;
  return createPortal(
    <div
      className={`overlay${asSlideOver ? ' slide-over' : ''} ${className}`}
      id={id}
      role={role}
      aria-modal='true'
      aria-labelledby={ariaLabelledBy}
      ref={ref}
      tabIndex={-1}
    >
      <div className='overlay-backdrop' onClick={onClose} />
      {children}
    </div>,
    typeof window !== 'undefined' && document.body
      ? document.body
      : ref.current || document.createElement('div'),
  );
};

export default Overlay;
